import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence, useAnimationControls, type Variants } from 'framer-motion';
import { useEffect } from 'react';
import LeadCardVisual from './LeadCardVisual';
import { PRIMARY, EASE_OUT_QUINT, EASE_MATERIAL } from './howItWorksConstants';
import { BLOCK_WIDTH, Arrow, Caption, Cursor, FacebookAd, type CursorState } from './DemoShared';

interface TypingFieldProps {
  label: string;
  value: string;
  shown: string;
  active: boolean;
}

const TypingField = ({ label, value, shown, active }: TypingFieldProps) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: '#666', mb: 0.4 }}>{label}</Typography>
    <Box
      sx={{
        border: '1px solid',
        borderColor: active ? PRIMARY : '#e5e5e5',
        borderRadius: 1,
        px: 1.2,
        py: 0.9,
        fontSize: 13,
        bgcolor: '#fff',
        minHeight: 36,
        color: shown ? '#1a1a1a' : '#bbb',
        transition: 'border-color 200ms',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {shown || value}
      {active && shown.length < value.length && shown.length > 0 && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '1px',
            height: 13,
            bgcolor: PRIMARY,
            ml: 0.3,
            verticalAlign: 'middle',
            animation: 'blink 0.8s infinite',
            '@keyframes blink': {
              '0%, 50%': { opacity: 1 },
              '51%, 100%': { opacity: 0 },
            },
          }}
        />
      )}
    </Box>
  </Box>
);

interface FormViewProps {
  nameShown: string;
  lastnameShown: string;
  phoneShown: string;
  nameActive: boolean;
  lastnameActive: boolean;
  phoneActive: boolean;
  submitted: boolean;
  submitPulse: boolean;
  cursor: CursorState;
}

const FormView = ({
  nameShown,
  lastnameShown,
  phoneShown,
  nameActive,
  lastnameActive,
  phoneActive,
  submitted,
  submitPulse,
  cursor,
}: FormViewProps) => {
  const submitControls = useAnimationControls();
  const submitVariants: Variants = {
    idle: { scale: 1 },
    pulse: {
      scale: [1, 0.96, 1.04, 1],
      transition: { duration: 0.4, ease: EASE_MATERIAL as [number, number, number, number] },
    },
  };

  useEffect(() => {
    if (submitPulse) {
      submitControls.start('pulse');
    }
  }, [submitPulse, submitControls]);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
        p: 2,
        width: BLOCK_WIDTH,
        position: 'relative',
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: '#1a1a1a',
          mb: 0.3,
          lineHeight: 1.3,
        }}
      >
        Votre demande de rappel
      </Typography>
      <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1.8 }}>
        Pour en savoir plus sur les séances.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 1.8 }}>
        <TypingField label="Prénom" value="Marie" shown={nameShown} active={nameActive} />
        <TypingField label="Nom" value="Bernard" shown={lastnameShown} active={lastnameActive} />
        <TypingField
          label="Téléphone"
          value="06 12 34 56 78"
          shown={phoneShown}
          active={phoneActive}
        />
      </Box>
      <Box component={motion.div} variants={submitVariants} animate={submitControls} initial="idle">
        <Box
          sx={{
            bgcolor: submitted ? PRIMARY : '#cbd5e1',
            color: '#fff',
            py: 1.1,
            borderRadius: 1.5,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 700,
            transition: 'background-color 300ms',
            cursor: 'default',
          }}
        >
          Envoyer
        </Box>
      </Box>
      <Cursor visible={cursor.visible} clicking={cursor.clicking} bottom={20} right={20} />
    </Box>
  );
};

const DashboardView = ({ showCard, subscribed }: { showCard: boolean; subscribed: boolean }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      borderRadius: 2,
      border: '1px solid #e5e7eb',
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      width: BLOCK_WIDTH,
    }}
  >
    <Box
      sx={{
        bgcolor: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        px: 1.8,
        py: 1.2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
          Mes demandes
        </Typography>
      </Box>
    </Box>
    <Box sx={{ p: 1.5, minHeight: 140 }}>
      <AnimatePresence mode="wait">
        {showCard ? (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{
              duration: 0.55,
              ease: EASE_OUT_QUINT as [number, number, number, number],
            }}
          >
            <LeadCardVisual
              firstname="Marie"
              lastname="Bernard"
              phone="06 12 34 56 78"
              state={subscribed ? 'available' : 'priority-locked'}
              timerSeconds={subscribed ? undefined : 10800}
              reveal={subscribed}
              highlight={subscribed}
            />
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Box
              sx={{
                py: 4,
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: 12,
              }}
            >
              En attente…
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  </Box>
);

// Affichage direct du résultat final (3 étapes), sans animation de lecture.
// En desktop, les 3 blocs (3 × BLOCK_WIDTH + flèches) dépassent la largeur de la popup
// "Comment ça marche". On réduit l'ensemble via `zoom` (reflow propre) pour que ça tienne
// sans scroll horizontal, plutôt qu'un transform scale qui laisserait un vide en dessous.
const Demo1 = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: 'center',
      justifyContent: 'center',
      gap: { xs: 2, md: 1.5 },
      flexWrap: 'nowrap',
      overflowX: { xs: 'visible', md: 'hidden' },
      zoom: { xs: 1, md: 0.78 },
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <FacebookAd cursor={{ visible: false, clicking: false }} />
      <Caption number={1}>Le prospect voit une pub facebook</Caption>
    </Box>

    <Arrow />

    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <FormView
        nameShown="Marie"
        lastnameShown="Bernard"
        phoneShown="06 12 34 56 78"
        nameActive={false}
        lastnameActive={false}
        phoneActive={false}
        submitted
        submitPulse={false}
        cursor={{ visible: false, clicking: false }}
      />
      <Caption number={2}>Il remplit un formulaire</Caption>
    </Box>

    <Arrow />

    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <DashboardView showCard subscribed />
      <Caption number={3}>Vous rappelez le prospect</Caption>
    </Box>
  </Box>
);

export default Demo1;
